import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import Topbar from "../components/Topbar";
import TaskCard from "../components/TaskCard";
import TaskDetailPanel from "../components/TaskDetailPanel";
import { AvatarStack } from "../components/Avatar";
import { useWorkspace } from "../contexts/WorkspaceContext";
import {
  Star, Lock, Plus, LayoutGrid, List, Calendar as CalendarIcon, BarChart3,
  Settings, Folder, Sparkles,
} from "lucide-react";

const VIEWS = [
  { id: "board", label: "Board", icon: LayoutGrid },
  { id: "list", label: "List", icon: List },
  { id: "calendar", label: "Calendar", icon: CalendarIcon },
  { id: "gantt", label: "Gantt", icon: BarChart3 },
];

const BRAND_COLOR = {
  luxury: "#7C2D12",
  elegance: "#1E3A8A",
  resort: "#9F1239",
  smart: "#DC2626",
};

export default function ProjectDetail() {
  const { projectId } = useParams();
  const [search, setSearch] = useSearchParams();
  const nav = useNavigate();
  const { members } = useWorkspace();
  const [project, setProject] = useState(null);
  const [tasklists, setTasklists] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [view, setView] = useState("board");
  const [activeTaskId, setActiveTaskId] = useState(search.get("task"));
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverList, setDragOverList] = useState(null);
  const [dragOverTaskId, setDragOverTaskId] = useState(null);
  const [newTask, setNewTask] = useState({}); // { tasklistId: "title" }
  const [newColumn, setNewColumn] = useState("");

  const reload = async () => {
    if (!projectId) return;
    const [p, tls, ts] = await Promise.all([
      api.get(`/projects/${projectId}`),
      api.get(`/projects/${projectId}/tasklists`),
      api.get(`/projects/${projectId}/tasks`),
    ]);
    setProject(p.data);
    setTasklists(tls.data);
    setTasks(ts.data);
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    setActiveTaskId(search.get("task"));
  }, [search]);

  const memberObjs = useMemo(
    () => members.filter((m) => m), [members]
  );

  if (!project) {
    return (
      <div>
        <Topbar title="Loading..." />
        <div className="p-8 text-zinc-500">Loading project…</div>
      </div>
    );
  }

  const onToggleTask = async (task) => {
    await api.patch(`/tasks/${task.id}`, { status: task.status === "done" ? "open" : "done" });
    reload();
  };

  const onDragStart = (e, task) => {
    setDraggingId(task.id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragEnd = () => {
    setDraggingId(null);
    setDragOverList(null);
    setDragOverTaskId(null);
  };
  const onDropToList = async (e, tasklistId) => {
    e.preventDefault();
    if (!draggingId) return;
    // Build new ordered list of tasks for the target column
    const draggedTask = tasks.find((t) => t.id === draggingId);
    if (!draggedTask) return;

    const targetList = topLevelTasks.filter((t) => t.tasklist_id === tasklistId && t.id !== draggingId);
    let insertIdx = targetList.length;
    if (dragOverTaskId && dragOverTaskId !== draggingId) {
      const i = targetList.findIndex((t) => t.id === dragOverTaskId);
      if (i >= 0) insertIdx = i;
    }
    const ordered = [...targetList.slice(0, insertIdx), draggedTask, ...targetList.slice(insertIdx)];
    const ids = ordered.map((t) => t.id);

    setDraggingId(null);
    setDragOverList(null);
    setDragOverTaskId(null);
    await api.post(`/tasklists/${tasklistId}/reorder`, { tasklist_id: tasklistId, task_ids_in_order: ids });
    reload();
  };

  const addTask = async (tasklistId) => {
    const title = (newTask[tasklistId] || "").trim();
    if (!title) return;
    await api.post("/tasks", {
      project_id: projectId, tasklist_id: tasklistId, title,
    });
    setNewTask({ ...newTask, [tasklistId]: "" });
    reload();
  };

  const addTasklist = async () => {
    if (!newColumn.trim()) return;
    await api.post(`/projects/${projectId}/tasklists`, { name: newColumn });
    setNewColumn("");
    reload();
  };

  const star = async () => {
    await api.post(`/projects/${projectId}/star`);
    reload();
  };

  const topLevelTasks = tasks.filter((t) => !t.parent_task_id);
  const tasksByList = (tlid) => topLevelTasks.filter((t) => t.tasklist_id === tlid);

  const openTask = (id) => { setActiveTaskId(id); setSearch({ task: id }); };
  const closeTask = () => { setActiveTaskId(null); setSearch({}); reload(); };

  return (
    <div data-testid="project-detail-page" className="flex flex-col h-screen">
      <Topbar
        title={
          <span className="flex items-center gap-2 flex-wrap">
            <button onClick={star} data-testid="project-star" className={project.starred_by?.includes(window.__user?.id) ? "text-[#D4A968]" : "text-zinc-600 hover:text-[#D4A968]"}>
              <Star className="w-4 h-4" />
            </button>
            {project.is_private && <Lock className="w-4 h-4 text-zinc-500" />}
            {project.code && <span className="text-zinc-500 font-mono">{project.code}</span>}
            <span className="text-white">— {project.name.split("–")[1]?.trim() || project.name}</span>
            {project.brand && (
              <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border" style={{
                color: BRAND_COLOR[project.brand], borderColor: BRAND_COLOR[project.brand] + "55", background: BRAND_COLOR[project.brand] + "20",
              }}>{project.brand}</span>
            )}
          </span>
        }
        breadcrumb="Projects"
        right={
          <button
            onClick={() => nav(`/ai-assistant?project=${projectId}`)}
            data-testid="project-ai-btn"
            className="flex items-center gap-1.5 bg-[#141417] border border-[#D4A968]/30 hover:border-[#D4A968] text-[#D4A968] px-3 py-2 rounded-md text-xs font-semibold transition-colors"
          >
            <Sparkles className="w-3.5 h-3.5" /> AI Assistant
          </button>
        }
      />

      {/* Sub-toolbar: views + members */}
      <div className="border-b border-zinc-800 px-6 py-2 flex items-center justify-between bg-[#0B0B0E]">
        <div className="flex items-center gap-1 bg-[#141417] border border-zinc-800 rounded-md p-0.5" data-testid="view-switcher">
          {VIEWS.map((v) => {
            const Icon = v.icon;
            const active = view === v.id;
            return (
              <button
                key={v.id}
                onClick={() => setView(v.id)}
                data-testid={`view-${v.id}`}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded transition-colors ${
                  active ? "bg-[#1B1B1F] text-white" : "text-zinc-500 hover:text-white"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {v.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            {topLevelTasks.length} tasks · {topLevelTasks.filter((t) => t.status === "done").length} done
          </span>
          <AvatarStack users={memberObjs.slice(0, 6)} max={5} size="sm" />
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        {view === "board" && (
          <div className="h-full overflow-x-auto p-4" data-testid="board-view">
            <div className="flex gap-3 h-full">
              {tasklists.map((tl) => {
                const list = tasksByList(tl.id);
                return (
                  <div
                    key={tl.id}
                    onDragOver={(e) => { e.preventDefault(); setDragOverList(tl.id); }}
                    onDragLeave={() => setDragOverList(null)}
                    onDrop={(e) => onDropToList(e, tl.id)}
                    data-testid={`column-${tl.id}`}
                    className={`w-72 shrink-0 kanban-col-bg border rounded-lg flex flex-col ${
                      dragOverList === tl.id ? "drop-target" : "border-zinc-800"
                    }`}
                  >
                    <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="brand-dot" style={{ background: tl.color || "#71717A" }} />
                        <span className="text-xs font-bold text-white">{tl.name}</span>
                      </div>
                      <span className="text-[10px] font-mono text-zinc-500 bg-[#0B0B0E] border border-zinc-800 px-1.5 rounded" data-testid={`column-count-${tl.id}`}>
                        {list.length}
                      </span>
                    </div>

                    <div className="flex-1 overflow-y-auto p-2 space-y-2">
                      {list.map((t) => (
                        <div
                          key={t.id}
                          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverTaskId(t.id); setDragOverList(tl.id); }}
                          className={dragOverTaskId === t.id && draggingId && draggingId !== t.id ? "border-t-2 border-[#D4A968] -mt-px pt-px" : ""}
                          data-testid={`task-slot-${t.id}`}
                        >
                          <TaskCard
                            task={t}
                            isDragging={draggingId === t.id}
                            onDragStart={(e) => onDragStart(e, t)}
                            onDragEnd={onDragEnd}
                            onClick={() => openTask(t.id)}
                            onToggle={onToggleTask}
                          />
                        </div>
                      ))}
                    </div>

                    <div className="p-2 border-t border-zinc-800">
                      <input
                        value={newTask[tl.id] || ""}
                        onChange={(e) => setNewTask({ ...newTask, [tl.id]: e.target.value })}
                        onKeyDown={(e) => e.key === "Enter" && addTask(tl.id)}
                        placeholder="+ Add task"
                        data-testid={`add-task-${tl.id}`}
                        className="w-full bg-transparent text-sm text-zinc-300 placeholder:text-zinc-600 outline-none px-1.5 py-1"
                      />
                    </div>
                  </div>
                );
              })}

              <div className="w-72 shrink-0">
                <div className="bg-[#141417]/60 border border-dashed border-zinc-800 rounded-lg p-3">
                  <div className="text-[10px] uppercase font-mono tracking-widest text-zinc-500 mb-2">New column</div>
                  <input
                    value={newColumn}
                    onChange={(e) => setNewColumn(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addTasklist()}
                    placeholder="e.g. Q4"
                    data-testid="new-column-input"
                    className="w-full bg-[#0B0B0E] border border-zinc-800 focus:border-[#D4A968] rounded px-2 py-1.5 text-sm text-white outline-none"
                  />
                  <button onClick={addTasklist} data-testid="new-column-btn" className="mt-2 w-full text-xs bg-[#1B1B1F] hover:bg-[#27272A] border border-zinc-800 py-1.5 rounded text-zinc-300">
                    Add column
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {view === "list" && (
          <div className="h-full overflow-y-auto p-4" data-testid="list-view">
            <div className="bg-[#141417] border border-zinc-800 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 bg-[#0B0B0E] border-b border-zinc-800">
                    <th className="text-left px-3 py-2 w-8"></th>
                    <th className="text-left px-3 py-2">Task</th>
                    <th className="text-left px-3 py-2">Tasklist</th>
                    <th className="text-left px-3 py-2">Priority</th>
                    <th className="text-left px-3 py-2">Due</th>
                    <th className="text-left px-3 py-2">Assignees</th>
                  </tr>
                </thead>
                <tbody>
                  {topLevelTasks.map((t) => {
                    const tl = tasklists.find((x) => x.id === t.tasklist_id);
                    const assigned = (t.assignees || []).map((id) => members.find((m) => m.id === id)).filter(Boolean);
                    return (
                      <tr
                        key={t.id}
                        onClick={() => openTask(t.id)}
                        className="border-b border-zinc-800 hover:bg-[#1B1B1F] cursor-pointer"
                        data-testid={`list-row-${t.id}`}
                      >
                        <td className="px-3 py-2">
                          <input type="checkbox" checked={t.status === "done"} onChange={(e) => { e.stopPropagation(); onToggleTask(t); }} onClick={(e) => e.stopPropagation()} className="accent-[#D4A968]" />
                        </td>
                        <td className={`px-3 py-2 ${t.status === "done" ? "line-through text-zinc-500" : "text-white"}`}>{t.title}</td>
                        <td className="px-3 py-2 text-zinc-400 text-xs">{tl?.name}</td>
                        <td className="px-3 py-2 text-xs uppercase font-mono">{t.priority}</td>
                        <td className="px-3 py-2 text-zinc-400 font-mono text-xs">{t.due_date || "—"}</td>
                        <td className="px-3 py-2"><AvatarStack users={assigned} max={3} size="xs" /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {view === "calendar" && (
          <CalendarView tasks={topLevelTasks} onOpenTask={openTask} />
        )}

        {view === "gantt" && (
          <GanttView project={project} tasklists={tasklists} tasks={topLevelTasks} onOpenTask={openTask} />
        )}
      </div>

      {activeTaskId && (
        <TaskDetailPanel
          taskId={activeTaskId}
          onClose={closeTask}
          onUpdated={() => reload()}
          onDeleted={() => reload()}
        />
      )}
    </div>
  );
}

function CalendarView({ tasks, onOpenTask }) {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const start = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const end = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  const startWeekday = (start.getDay() + 6) % 7; // Mon=0
  const days = end.getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
  while (cells.length % 7) cells.push(null);

  const monthLabel = cursor.toLocaleString("default", { month: "long", year: "numeric" });

  const tasksOn = (date) => tasks.filter((t) => t.due_date === date.toISOString().slice(0, 10));

  return (
    <div className="h-full p-4 overflow-y-auto" data-testid="calendar-view">
      <div className="bg-[#141417] border border-zinc-800 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
          <h3 className="text-sm font-bold text-white">{monthLabel}</h3>
          <div className="flex gap-1 text-xs">
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1))} className="px-2 py-1 hover:bg-[#1B1B1F] rounded text-zinc-300">‹</button>
            <button onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))} className="px-2 py-1 hover:bg-[#1B1B1F] rounded text-zinc-300">Today</button>
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1))} className="px-2 py-1 hover:bg-[#1B1B1F] rounded text-zinc-300">›</button>
          </div>
        </div>
        <div className="grid grid-cols-7 text-[10px] uppercase font-mono tracking-widest text-zinc-500 border-b border-zinc-800">
          {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => (
            <div key={d} className="px-2 py-1.5">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((c, i) => {
            if (!c) return <div key={i} className="min-h-[88px] border-b border-r border-zinc-800 bg-[#0B0B0E]" />;
            const isToday = c.toDateString() === today.toDateString();
            const list = tasksOn(c);
            return (
              <div key={i} className={`min-h-[88px] border-b border-r border-zinc-800 p-1.5 ${isToday ? "bg-[#D4A968]/5" : ""}`}>
                <div className={`text-[10px] font-mono ${isToday ? "text-[#D4A968]" : "text-zinc-500"}`}>{c.getDate()}</div>
                <div className="space-y-0.5 mt-1">
                  {list.slice(0, 3).map((t) => (
                    <button
                      key={t.id}
                      onClick={() => onOpenTask(t.id)}
                      data-testid={`cal-task-${t.id}`}
                      className="block w-full text-left text-[10px] truncate bg-[#1B1B1F] hover:bg-[#27272A] border border-zinc-800 rounded px-1 py-0.5 text-zinc-200"
                    >
                      {t.title}
                    </button>
                  ))}
                  {list.length > 3 && <div className="text-[9px] text-zinc-500 px-1">+{list.length - 3} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function GanttView({ project, tasklists, tasks, onOpenTask }) {
  if (!project.start_date || !project.due_date) {
    return <div className="p-6 text-zinc-500 text-sm" data-testid="gantt-empty">Project needs start & due dates for Gantt view.</div>;
  }
  const start = new Date(project.start_date);
  const end = new Date(project.due_date);
  const totalMs = end - start || 1;
  const totalDays = Math.max(1, Math.round(totalMs / (1000 * 60 * 60 * 24)));

  const ROW_H = 32;

  return (
    <div className="h-full overflow-auto p-4" data-testid="gantt-view">
      <div className="bg-[#141417] border border-zinc-800 rounded-lg p-4">
        <div className="text-[10px] uppercase font-mono tracking-widest text-zinc-500 mb-3">
          Timeline · {project.start_date} → {project.due_date} · {totalDays} days
        </div>
        <div className="space-y-1.5 min-w-[800px]">
          {tasklists.map((tl) => {
            const list = tasks.filter((t) => t.tasklist_id === tl.id && t.due_date);
            return (
              <div key={tl.id}>
                <div className="text-[10px] uppercase font-mono tracking-widest text-zinc-500 my-1.5">{tl.name}</div>
                {list.map((t) => {
                  const due = new Date(t.due_date);
                  const offsetDays = Math.max(0, Math.round((due - start) / (1000 * 60 * 60 * 24)) - 5);
                  const widthDays = 6;
                  const leftPct = (offsetDays / totalDays) * 100;
                  const widthPct = (widthDays / totalDays) * 100;
                  return (
                    <div key={t.id} className="flex items-center gap-3 hover:bg-[#1B1B1F] rounded px-1.5 cursor-pointer" onClick={() => onOpenTask(t.id)} style={{ height: ROW_H }} data-testid={`gantt-row-${t.id}`}>
                      <div className="w-64 truncate text-xs text-zinc-300">{t.title}</div>
                      <div className="relative flex-1 h-2 bg-[#0B0B0E] border border-zinc-800 rounded">
                        <div
                          className="absolute h-full rounded"
                          style={{
                            left: `${leftPct}%`,
                            width: `${widthPct}%`,
                            background: t.status === "done" ? "#10B981" : t.priority === "urgent" ? "#EF4444" : t.priority === "high" ? "#F59E0B" : "#D4A968",
                          }}
                        />
                      </div>
                      <div className="w-24 text-[10px] font-mono text-zinc-500 text-right">{t.due_date}</div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
